import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'; // Admin uses Heroicons
import ConfirmDialog from '../components/ConfirmDialog';
import { Tab } from '@headlessui/react'; // Assuming HeadlessUI is used or just simple manual tabs
import { Save } from 'lucide-react'; // Using Lucide for Save icon consistency if needed

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const DSAOnboardingRequirementsPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [settings, setSettings] = useState({
        section_title: 'Who Can Apply & What You Need',
        section_subtitle: 'Requirements',
        cta_text: 'To ensure transparency and regulatory compliance, we require basic documentation and verification.',
        cta_link: 'All applications are subject to due diligence and approval.'
    });

    const [eligibilityList, setEligibilityList] = useState([]);
    const [documentsList, setDocumentsList] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('eligibility'); // 'eligibility' or 'documents'
    const [editingItem, setEditingItem] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        description: '', // used for subtitle in documents
        icon_name: '',
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, type: null });

    const eligibilityLoanType = 'dsa_eligibility';
    const documentsLoanType = 'dsa_documents';
    const settingsLoanType = 'dsa_requirements';

    const iconOptions = [
        'CreditCard', 'FileText', 'CheckCircle2', 'Camera', 'MapPin', 'Building2', 'User', 'Shield'
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, eligibilityRes, documentsRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/settings/${settingsLoanType}`),
                axios.get(`/api/admin/key-benefits/${eligibilityLoanType}`),
                axios.get(`/api/admin/key-benefits/${documentsLoanType}`)
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
            if (eligibilityRes.data.success) {
                setEligibilityList(eligibilityRes.data.data || []);
            }
            if (documentsRes.data.success) {
                setDocumentsList(documentsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsSave = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put(`/api/admin/key-benefits/settings/${settingsLoanType}`, settings);
            toast.success('Settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleAddNew = (type) => {
        setModalType(type);
        setEditingItem(null);
        setModalData({
            title: '',
            description: '',
            icon_name: type === 'documents' ? 'FileText' : '',
            display_order: (type === 'eligibility' ? eligibilityList.length : documentsList.length) + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (item, type) => {
        setModalType(type);
        setEditingItem(item);
        setModalData({
            title: item.title,
            description: item.description || '',
            icon_name: item.icon_name || '',
            display_order: item.display_order,
            is_active: item.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id, type) => {
        setDeleteConfirm({ isOpen: true, id, type });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Item deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting item:', error);
            toast.error('Failed to delete item');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null, type: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const currentLoanType = modalType === 'eligibility' ? eligibilityLoanType : documentsLoanType;
            const payload = { ...modalData, loan_type: currentLoanType };

            if (editingItem) {
                await axios.put(`/api/admin/key-benefits/${editingItem.id}`, payload);
                toast.success('Updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving item:', error);
            toast.error('Failed to save item');
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Requirements</h1>
                    <p className="text-slate-500">Manage Eligibility and Documents</p>
                </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Header Settings</h2>
                <form onSubmit={handleSettingsSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Subtitle / Tag</label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                            value={settings.cta_text || ''}
                            onChange={(e) => setSettings({ ...settings, cta_text: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            rows={2}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Note (Bottom Box)</label>
                        <input
                            type="text"
                            value={settings.cta_link || ''}
                            onChange={(e) => setSettings({ ...settings, cta_link: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., All applications are subject to due diligence and approval."
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                        >
                            <Save size={18} />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Eligibility Criteria List */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-bold text-slate-800">Eligibility Criteria</h2>
                        <button
                            onClick={() => handleAddNew('eligibility')}
                            className="flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-200 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" /> Add
                        </button>
                    </div>
                    <div className="space-y-3">
                        {eligibilityList.map((item) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all">
                                <span className="font-medium text-slate-700">{item.title}</span>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEdit(item, 'eligibility')} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <PencilIcon className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(item.id, 'eligibility')} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {eligibilityList.length === 0 && <p className="text-center text-slate-500 py-4">No criteria added.</p>}
                    </div>
                </div>

                {/* Documents List */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-bold text-slate-800">Documents Required</h2>
                        <button
                            onClick={() => handleAddNew('documents')}
                            className="flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-200 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" /> Add
                        </button>
                    </div>
                    <div className="space-y-3">
                        {documentsList.map((item) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all">
                                <div>
                                    <div className="font-medium text-slate-800">{item.title}</div>
                                    <div className="text-xs text-slate-500">{item.description}</div>
                                    <div className="text-xs text-primary-600 font-mono mt-1">{item.icon_name}</div>
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEdit(item, 'documents')} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <PencilIcon className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(item.id, 'documents')} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {documentsList.length === 0 && <p className="text-center text-slate-500 py-4">No documents added.</p>}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold mb-6">
                            {editingItem ? 'Edit' : 'Add'} {modalType === 'eligibility' ? 'Criteria' : 'Document'}
                        </h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Title</label>
                                <input
                                    type="text"
                                    value={modalData.title}
                                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    required
                                />
                            </div>

                            {modalType === 'documents' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Subtitle / Description</label>
                                        <input
                                            type="text"
                                            value={modalData.description}
                                            onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-xl"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Icon (Lucide Name)</label>
                                        <select
                                            value={modalData.icon_name}
                                            onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                            className="w-full px-4 py-2 border rounded-xl font-mono"
                                        >
                                            <option value="">Select Icon</option>
                                            {iconOptions.map(icon => (
                                                <option key={icon} value={icon}>{icon}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border rounded-xl hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, type: null })}
                onConfirm={confirmDelete}
                title="Delete Item"
                message="Are you sure? This cannot be undone."
            />
        </div>
    );
};

export default DSAOnboardingRequirementsPage;
